import {
  makeGroup,
  makeHull,
  makeLine,
  makeTransform,
} from 'mint-turn';

import Mat4 from 'gl-mat4';
import Vec3 from 'gl-vec3';
import Vec2 from 'gl-vec2';

import { lerp, lerpAngle, lerpVec3 } from './lerp';

const makeCircleOfPoints = (radius, height, count) => {
  return [...Array(count).keys()].map((i) => {
    const angle = 2 * Math.PI * i / count;
    return [Math.sin(angle) * radius, height, Math.cos(angle) * radius];
  });
};

const makeLeg = () => {
  const leg = makeLine([0, -1, 0], [0, -0.3, 0]);
  const leftToe = makeLine([0, -1, 0], [-0.15, -1, 0.25]);
  const middleToe = makeLine([0, -1, 0], [0, -1, 0.35]);
  const rightToe = makeLine([0, -1, 0], [+0.15, -1, 0.25]);
  const group = makeGroup([0, 0, 0], [leg, leftToe, middleToe, rightToe]);
  return makeTransform(group, Mat4.fromTranslation([], [0.2, 0, 0]));
};

const makeEgg = () => {
  const count = 8;
  const egg = makeHull([
    [ 0, -1, 0 ],
    ...[...Array(count).keys()].map(i => {
      const angle = (Math.PI / 2) * (i / count);
      return makeCircleOfPoints(Math.cos(angle), -Math.sin(angle), 16);
    }).flat(),
    ...[...Array(count).keys()].map(i => {
      const angle = (Math.PI / 2) * (i / count);
      return makeCircleOfPoints(Math.cos(angle), Math.sin(angle) * 1.5, 16);
    }).flat(),
    [ 0, 1.5, 0 ],
  ]);
  egg[0].setAttribute('fill', 'whitesmoke');
  return makeTransform(egg, Mat4.fromTranslation([], [0, 1.7, 0]));
};

const makeEye = () => {
  const eye = makeGroup([0, 0, 0], [makeHull([
    ...makeCircleOfPoints(0.3, 0, 12),
    ...makeCircleOfPoints(0.3, 0.1, 12),
    ...makeCircleOfPoints(0.325, 0.05, 12),
    ...makeCircleOfPoints(0.25, 0, 8).map(([x, y, z]) => [x, y, z + 0.1]),
    ...makeCircleOfPoints(0.25, 0.1, 8).map(([x, y, z]) => [x, y, z + 0.1]),
    ...makeCircleOfPoints(0.275, 0.05, 8).map(([x, y, z]) => [x, y, z + 0.1]),
  ])]);
  eye[0].setAttribute('fill', 'white');
  const pupil = makeGroup([0, 0, 0], [makeHull(makeCircleOfPoints(0.1, 0.04, 6))]);
  pupil[0].setAttribute('fill', 'black');
  return makeTransform(makeGroup([0, 0, 0], [eye, pupil]), Mat4.fromRotation([], -Math.PI/2, [1, 0, 0]));
};

const makePlayer = (getFloorIntersection, getWallIntersection) => {
  const egg = makeEgg();
  
  const leftLegTransform = Mat4.create();
  const leftLeg = makeTransform(makeLeg(), leftLegTransform);
  const rightLegTransform = Mat4.create();
  const rightLeg = makeTransform(makeLeg(), rightLegTransform);
  
  const leftEyeTransform = Mat4.fromTranslation([], [0.25, 2.5, 0.8]);
  const leftEye = makeTransform(makeEye(), leftEyeTransform);
  const rightEyeTransform = Mat4.fromTranslation([], [-0.25, 2.5, 0.8]);
  const rightEye = makeTransform(makeEye(), rightEyeTransform);
  
  const shadowTransform = Mat4.create();
  const shadow = makeTransform(makeHull(makeCircleOfPoints(1, 0, 8)), shadowTransform);
  shadow[0].setAttribute('fill', 'black');
  shadow[0].setAttribute('opacity', 0);

  const group = makeGroup([0, 3, 0], [shadow, leftLeg, rightLeg, egg, leftEye, rightEye]);
  
  const transform = Mat4.create();
  const player = makeTransform(group, transform);
  player[0].setAttribute('stroke', 'black');
  player[0].setAttribute('stroke-width', 3);
  
  return [player, {
    base: transform,
    leftLeg: leftLegTransform,
    rightLeg: rightLegTransform,
    shadow: shadowTransform,
    setShadowOpacity: (opacity) => shadow[0].setAttribute('opacity', opacity),
  }];
};

export default makePlayer;